import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, FilePlus, Loader2, Maximize2, CheckCircle2, Trash2, RefreshCw, ScanSearch, AlertTriangle, RotateCw } from "lucide-react";
import { adSequence } from "@/lib/adMedia";
import { listPapers, uploadPaper, paperUrl, getHorse, deletePaper, replacePaper, scanPaper, requestPaperReview, rotateMedia } from "@/lib/api";
import { ConfirmDelete } from "@/components/adtemplate/ConfirmDelete";
import { RegistrationReview } from "@/components/adtemplate/RegistrationReview";
import { DropZone, splitFiles } from "@/components/apply/DropZone";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SubHead } from "@/components/AdPhotos";
import { Button } from "@/components/ui/button";

const EXTS = ["pdf", "jpg", "jpeg", "png", "heic", "heif", "webp"];
const isPdf = (p) => p.content_type === "application/pdf";

function ScanStatus({ p, scanning, onScan, onReview, onAsk, canApply }) {
  const s = p.scan;
  const t = `ad-paper-scan-${p.id}`;
  if (scanning) return <div data-testid={`${t}-busy`} className="px-3 pb-2 text-xs text-stone-600 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" />Scanning document…</div>;
  if (!s) return <button type="button" data-testid={`${t}-btn`} onClick={onScan} className="mx-3 mb-2 text-xs font-semibold text-amber-800 underline">Scan this document</button>;
  if (s.status === "recognized") return (
    <div data-testid={`${t}-ok`} className="px-3 pb-2 text-xs text-emerald-800 space-y-1">
      <div className="flex items-center gap-1.5"><ScanSearch className="h-3.5 w-3.5" />Recognised{s.registry ? ` as ${s.registry}` : ""} registration papers</div>
      {canApply && <button type="button" data-testid={`${t}-review`} onClick={onReview} className="font-semibold text-amber-800 underline">Review information found</button>}
    </div>
  );
  return (
    <div data-testid={`${t}-warn`} className="mx-2 mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 space-y-1.5">
      <div className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{s.message}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <button type="button" data-testid={`${t}-rescan`} onClick={onScan} className="font-semibold underline">Scan again</button>
        {s.review_requested ? <span data-testid={`${t}-review-requested`} className="font-semibold">Admin review requested</span>
          : <button type="button" data-testid={`${t}-ask-review`} onClick={onAsk} className="font-semibold underline">Request admin review</button>}
      </div>
    </div>
  );
}

export function AdPapers({ horseId, locked, registeredHint, media, onChange, info, onApplyInfo }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [review, setReview] = useState(null);
  const [scanning, setScanning] = useState(null);
  const scan = async (ids) => {
    let first = null;
    for (const mid of ids) {
      setScanning(mid);
      try { const s = await scanPaper(horseId, mid); if (s.status === "recognized" && !first) first = s; }
      catch (e) { toast.error(e.response?.data?.detail || "Could not scan this document"); }
    }
    setScanning(null);
    await load();
    if (first && onApplyInfo) setReview(first);
  };
  const askReview = async (p) => {
    try { await requestPaperReview(horseId, p.id); toast.success("Sent to our team for review"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not request review"); }
  };
  const [open, setOpen] = useState(null);
  const [turning, setTurning] = useState(null);
  // 90 degrees clockwise per click; saved on the server (original file untouched) and reflected in every preview
  const rotate = async (p) => {
    setTurning(p.id);
    try {
      const { rotation } = await rotateMedia(horseId, `paper-${p.id}`, "cw");
      setData((d) => ({ ...d, papers: d.papers.map((x) => (x.id === p.id ? { ...x, rotation } : x)) }));
      if (onChange && media) onChange(media.map((m) => (m.kind === "paper" && m.application_media_id === p.id ? { ...m, rotation } : m)));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rotate the registration paper"); }
    finally { setTurning(null); }
  };
  const [del, setDel] = useState(null);
  const fileRef = useRef(null);
  const [replacing, setReplacing] = useState(null);
  const remove = async () => {
    const p = del; setDel(null); setBusy(p.original_filename || "document");
    try { onChange(await deletePaper(horseId, p.id)); toast.success("Registration paper deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
    finally { setBusy(null); }
  };
  const replace = async (file) => {
    const p = replacing; setReplacing(null);
    if (!file || !p) return;
    const { ok, error } = splitFiles([file], EXTS, "Use a PDF or a photo (JPG, PNG, HEIC, WEBP).");
    if (error || !ok.length) { toast.error(error); return; }
    setBusy(file.name);
    try { onChange(await replacePaper(horseId, p.id, file)); toast.success("Registration paper replaced — scanning…"); const d = await listPapers(horseId); setData(d); const n = d.papers.find((x) => !data?.papers?.some((o) => o.id === x.id)); if (n) scan([n.id]); }
    catch (e) { toast.error(`${file.name}: ${e.response?.data?.detail || "upload failed"}`); }
    finally { setBusy(null); }
  };
  const load = useCallback(() => listPapers(horseId).then(setData).catch(() => setData({ registered: false, papers: [] })), [horseId]);
  useEffect(() => { load(); }, [load]);
  const missing = !!data?.papers?.some((p) => !(media || []).some((m) => m.kind === "paper" && m.application_media_id === p.id && m.ad_order));
  useEffect(() => {
    if (missing && onChange) { getHorse(horseId).then((h) => onChange(h.media)).catch(() => {}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missing, horseId]);

  const upload = async (files) => {
    const { ok, error } = splitFiles(files, EXTS, "Use a PDF or a photo (JPG, PNG, HEIC, WEBP).");
    if (error) toast.error(error);
    const added = [];
    for (const f of ok) {
      setBusy(f.name);
      try { added.push((await uploadPaper(horseId, f)).id); } catch (e) { toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(null);
    if (ok.length) { toast.success("Registration documents added — scanning…"); await load(); }
    if (added.length) scan(added);
  };

  const pos = (mid) => {
    const seq = adSequence(media);
    const i = seq.findIndex((m) => m.kind === "paper" && m.application_media_id === mid);
    return i >= 0 ? i + 1 : null;
  };

  if (!data || !(data.registered || registeredHint)) return null;
  return (
    <SubHead letter="A" title="Horse Registration Papers" testId="ad-papers-section">
      <p className="text-sm text-stone-600">Papers from your application are already here — no need to upload them again. Registration papers are <strong>always included</strong> in your advertisement, after your horse photos. Change their position in <strong>Advertisement Order</strong> below. Nothing is public until your ad is approved and posted.</p>
      {data.papers.length ? (
        <div data-testid="ad-papers-list" className="grid grid-cols-1 min-[440px]:grid-cols-2 sm:grid-cols-3 gap-3">
          {data.papers.map((p) => (
            <div key={p.id} className="rounded-lg border border-amber-700 bg-white overflow-hidden">
            <button type="button" data-testid={`ad-paper-${p.id}`} onClick={() => setOpen(p)}
              className="relative block w-full aspect-square bg-stone-50 group">
              {isPdf(p) ? (
                <span className="h-full w-full flex flex-col items-center justify-center gap-1 text-stone-600 p-2">
                  <FileText className="h-8 w-8 text-red-700" /><span className="text-[11px] font-semibold truncate max-w-full">{p.original_filename || "Document.pdf"}</span>
                </span>
              ) : <img src={paperUrl(horseId, p.id, p.rotation)} alt={p.original_filename || "Registration paper"} loading="lazy" className="h-full w-full object-cover" />}
              <Maximize2 className="absolute top-1 right-1 h-4 w-4 text-stone-700 opacity-70 group-hover:opacity-100" />
            </button>
            <div data-testid={`ad-paper-included-${p.id}`} className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-amber-900">
              <CheckCircle2 className="h-4 w-4 shrink-0" />Included in ad{pos(p.id) ? ` · #${pos(p.id)}` : ""}
            </div>
            <ScanStatus p={p} scanning={scanning === p.id} onScan={() => scan([p.id])} onReview={() => setReview(p.scan)} onAsk={() => askReview(p)} canApply={!!onApplyInfo && !locked} />
            {!locked && (
              <div className="flex border-t border-stone-200">
                {!isPdf(p) && (
                  <button type="button" data-testid={`ad-paper-rotate-${p.id}`} disabled={!!busy || turning === p.id} onClick={() => rotate(p)} title="Rotate 90° clockwise"
                    className="flex-1 h-11 inline-flex items-center justify-center gap-1 text-xs font-semibold text-stone-700 hover:bg-stone-50 border-r border-stone-200 disabled:opacity-40">
                    {turning === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}Rotate</button>
                )}
                <button type="button" data-testid={`ad-paper-replace-${p.id}`} disabled={!!busy} onClick={() => { setReplacing(p); fileRef.current?.click(); }}
                  className="flex-1 h-11 inline-flex items-center justify-center gap-1 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"><RefreshCw className="h-3.5 w-3.5" />Replace</button>
                <button type="button" data-testid={`ad-paper-delete-${p.id}`} disabled={!!busy} onClick={() => setDel(p)}
                  className="flex-1 h-11 inline-flex items-center justify-center gap-1 text-xs font-semibold text-red-700 hover:bg-red-50 border-l border-stone-200 disabled:opacity-40"><Trash2 className="h-3.5 w-3.5" />Delete</button>
              </div>
            )}
            </div>
          ))}
        </div>
      ) : <p data-testid="ad-papers-empty" className="text-sm text-stone-500">No registration papers on file yet.</p>}
      {!locked && (
        <DropZone testId="ad-paper-upload" accept={EXTS.map((e) => `.${e}`).join(",")} onFiles={upload} disabled={!!busy}
          icon={FilePlus} label="Add Registration Documents" dropLabel="Drop documents to add" />
      )}
      {data.papers.length > 0 && (
        <Button type="button" variant="outline" data-testid="scan-registration-papers" disabled={!!scanning} onClick={() => scan(data.papers.map((p) => p.id))} className="h-11 rounded-full">
          {scanning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanSearch className="h-4 w-4 mr-1.5" />}Scan Registration Papers
        </Button>
      )}
      {review && <RegistrationReview scan={review} info={info} onClose={() => setReview(null)} onApply={(patch) => { onApplyInfo(patch); setReview(null); toast.success("Horse Info updated from registration papers"); }} />}
      {busy && <div data-testid="ad-paper-uploading" className="flex items-center gap-2 text-sm text-stone-600"><Loader2 className="h-4 w-4 animate-spin" /> Working on {busy}…</div>}
      <input ref={fileRef} type="file" data-testid="ad-paper-replace-input" accept={EXTS.map((e) => `.${e}`).join(",")} className="hidden"
        onChange={(e) => { replace(e.target.files?.[0]); e.target.value = ""; }} />
      <ConfirmDelete item={del} kind="registration paper" onCancel={() => setDel(null)} onConfirm={remove}
        note="Papers from your original application stay on file with us; they just won't be in this ad." />
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="ad-paper-preview" className="max-w-3xl p-2 sm:p-3">
          <DialogTitle className="text-sm px-1">{open?.original_filename || "Registration paper"}</DialogTitle>
          <DialogDescription className="sr-only">Private registration document preview</DialogDescription>
          {open && (isPdf(open)
            ? <iframe title="Registration paper PDF" src={paperUrl(horseId, open.id)} className="w-full h-[75vh] rounded-lg border border-stone-200" />
            : <img src={paperUrl(horseId, open.id, open.rotation)} alt={open.original_filename} className="w-full max-h-[80vh] object-contain rounded-lg" />)}
          {open && <a data-testid="ad-paper-open-new" href={paperUrl(horseId, open.id, open.rotation)} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-amber-800 underline px-1">Open in new tab</a>}
        </DialogContent>
      </Dialog>
    </SubHead>
  );
}
